import { useState } from 'react';
import { useDispatch, useReduxState } from 'hooks';
import api from 'api';
import { breakpoints } from 'styles';
import { ModalButtonWithForm } from 'components/common';
import { UserSelector } from 'components/common/form';
import { UserPasswordReview } from 'components/users';

import type { GetAchievementGroupCompletionsRes } from 'shared-types/responses';

export type FormValues = {
  emails: string[];
  expirationInDays: number[];
  passwords: string[];
  usernames: string[];
  userSelector: 'emails' | 'usernames';
};

interface Props {
  groupCompletion: GetAchievementGroupCompletionsRes['groupCompletions'][number];
  updateGroups: () => Promise<void>;
}

const AchievementGroupOfUsersAddMembers = ({
  groupCompletion,
  updateGroups
}: Props) => {
  const { dispatch } = useDispatch();

  const {
    messages,
    pageState: { achievement },
    theme
  } = useReduxState();

  const [userPasswords, setUserPasswords] = useState(
    {} as Record<string, string>
  );

  const handleHide = () => setUserPasswords({});

  const submit = async (values: FormValues) => {
    try {
      if (!values.userSelector) values.userSelector = 'emails';

      const users = values[values.userSelector];

      const newMembers = [];
      for (let i = 0; i < users.length; i++) {
        const newUser = {
          ...(values.userSelector === 'emails'
            ? { email: users[i] }
            : { username: users[i] }),
          ...(values.passwords?.[i] && {
            password: values.passwords[i],
            expirationInDays: values.expirationInDays[i]
          })
        };

        newMembers.push(newUser);
      }

      const response = await api.post(
        `/admin/achievements/${achievement.id}` +
          `/group-completions/${groupCompletion.achievementData.id}/members`,
        { newMembers }
      );

      await updateGroups();

      let successMessage =
        `${messages.achievementGroupConversationAddParticipantsSuccess}` +
        (response.data.newUsers > 0
          ? `, ${response.data.newUsers} ${messages.settingsAddUsersAccountsCreated}`
          : '');
      dispatch.addNotice({ text: successMessage, type: 'success' });

      if (values.usernames?.length && response.data.newUsers) {
        const userPasswords: Record<string, string> = {};
        values.passwords.forEach((password, i) => {
          if (password) userPasswords[values.usernames[i]] = password;
        });

        setUserPasswords(userPasswords);
      }
    } catch (err) {
      await dispatch.handleErrorDispatch(err);
    }
  };

  const initialValues = { emails: [], usernames: [] };

  return (
    <>
      <ModalButtonWithForm
        buttonCategory="success"
        buttonIcon="user-plus"
        buttonIsDropdown={true}
        buttonSize="header"
        buttonText={messages.achievementGroupConversationAddParticipants}
        buttonTextHideBreakpoint={breakpoints.medium - 1}
        contentStyle={{ padding: 0 }}
        initialValues={initialValues}
        modalActionText={messages.commonAddUsers}
        modalTitle={messages.achievementGroupConversationAddParticipants}
        modalWidth="wide"
        onHide={handleHide}
        onSubmit={submit}
      >
        {() => (
          <div style={{ padding: 15 }}>
            <UserSelector
              existingUsers={groupCompletion.members.map((member) => member.id)}
            />
          </div>
        )}
      </ModalButtonWithForm>

      <UserPasswordReview
        onModalDismiss={handleHide}
        passwords={userPasswords}
      />

      <style jsx>{`
        .header {
          border-top: 1px solid ${theme.lightGreyDark};
          padding: 15px 15px 20px;
        }
        .options {
          align-items: flex-start;
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          gap: 20px;
          width: 100%;
        }
      `}</style>
    </>
  );
};

export default AchievementGroupOfUsersAddMembers;
